/**
 * Resolves a PDF guide URL to a fully-qualified https URL.
 *
 * - Accepts a relative path (e.g. "/guides/foo.pdf"), hostname path
 *   (e.g. "d123.cloudfront.net/guides/foo.pdf"), or full URL.
 * - If a base URL is configured via NEXT_PUBLIC_PDF_GUIDE_BASE_URL,
 *   the path is resolved against it (typically a CloudFront domain).
 * - Safely handles null, undefined, and empty values by returning an empty string.
 *
 * Usage:
 *   resolvePdfGuideUrl("/guides/foo.pdf")
 *   resolvePdfGuideUrl("d123abc.cloudfront.net/guides/foo.pdf")
 *   resolvePdfGuideUrl("https://d123abc.cloudfront.net/guides/foo.pdf")
 */

export const resolvePdfGuideUrl = (path?: null | string): string => {
  if (!path?.trim()) return '';

  const trimmed = path.trim();

  // Already a full URL
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  // Protocol-relative URL
  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`;
  }

  const normalizeBase = (base?: string): null | string => {
    if (!base?.trim()) return null;
    return base.startsWith('http') ? base : `https://${base}`;
  };

  // Resolve with base URL if configured
  const baseUrl = normalizeBase(process.env.NEXT_PUBLIC_PDF_GUIDE_BASE_URL);
  if (baseUrl) {
    try {
      return new URL(trimmed, baseUrl).toString();
    } catch {
      return '';
    }
  }

  // No base URL - if it looks like a hostname, prepend https://
  if (/^[a-z0-9.-]+\.cloudfront\.net/i.test(trimmed)) {
    return `https://${trimmed}`;
  }

  return '';
};
